import React from "react";
import styled from "styled-components";
import Rule from "../../../Rule";
import Image from "../../../Image";
import Links from "../../../Links";
import Heading from "../../../Heading";
import ColumnHeading from "../ColumnHeading";
import CaseStudiesCard from "../CaseStudiesCard";
import DesignSystemsProcessDiagram from "../../../../img/casestudy/design-systems/design-system-process-diagram.jpg";
import DesignSystemsDistillInsights from "../../../../img/casestudy/design-systems/design-system-distill-insights.jpg";
import DesignSystemsRoadmapPlan from "../../../../img/casestudy/design-systems/design-system-roadmap-plan.png";
import OutcomeBasedRoadmapImage from "../../../../img/casestudy/design-systems/design-system-outcomes.png";
import Box from "../../../Box";
import BlogLayout from "../BlogLayout";
import Stack from "../../../Stack";

const ObjectiveExample = styled.div`
  padding: 3.6rem 2.4rem;
  border-radius: var(--radius-card);
  border: var(--border-thin-inverted);
  position: relative;
  margin-top: 3.2rem;
  background: #2d2d2d;
  font-size: var(--font-size-text-small);
  &:before {
    content: "Roadmap Item Example";
    position: absolute;
    top: -12px;
    padding: 0 8px;
    background: #242424;
    background: linear-gradient(180deg, #242424 50%, #2d2d2d 50%);
    left: 8px;
  }
`;

const RoadmapExample = styled.div`
  padding: 3.6rem 2.4rem;
  border-radius: var(--radius-card);
  border: var(--border-thin-inverted);
  position: relative;
  margin-top: 3.2rem;
  background: #2d2d2d;
  font-size: var(--font-size-text-small);
  &:before {
    content: "Roadmap Example";
    position: absolute;
    top: -12px;
    padding: 0 8px;
    background: #242424;
    background: linear-gradient(180deg, #242424 50%, #2d2d2d 50%);
    left: 8px;
  }
`;

const RoadmapColumnBox = styled.div`
  margin-bottom: var(--spacing-xx-large);
  position: relative;
  &:last-child {
    margin-bottom: 0;
  }
  @media (min-width: 1400px) {
    display: grid;
    grid-gap: var(--spacing-large);
    grid-template-columns: 34rem 1fr;
  }
  p,
  ol,
  ul {
    max-width: 64ch;
    &:last-child,
    li:last-child {
      margin-bottom: var(--spacing-clear);
    }
  }
  p,
  ol,
  ul {
    max-width: 64ch;
  }
`;

const DiscoveryCluesInsights = styled(Box)`
  display: grid;
  align-items: start;
  gap: var(--spacing-large);
  @media (min-width: 1300px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Insight = styled.span`
  color: #f788de;
  font-weight: bold;
`;

const Do = styled.span`
  color: #88c1f7;
  font-weight: bold;
`;

const Think = styled.span`
  color: #9788f8;
  font-weight: bold;
`;

const Feel = styled.span`
  color: #84bd50;
  font-weight: bold;
`;

const DesigningTheRoadmap = (
  <CaseStudiesCard id="roadmap">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Case Study
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Discovery-led roadmap creation to align the Feelix Design System team around a shared vision.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout wide>
      <Heading>Feelix Roadmap</Heading>
      <p style={{ maxWidth: "58ch" }}>
        To create the Feelix roadmap, I employed a mix of techniques to support discovery, distil insights, and define a
        clear vision for the Design System to work towards. This approach drew on methods from James Kalbach’s{" "}
        <Links target="_blank" rel="noopener noreferrer" href="http://shop.oreilly.com/product/0636920038870.do">
          Mapping Experiences
        </Links>
        , Jeff Gothelf’s blog on{" "}
        <Links
          target="_blank"
          rel="noopener noreferrer"
          href="https://medium.com/@jboogie/what-does-an-agile-product-roadmap-look-like-cf0dbe5be4ef"
        >
          Agile product roadmapping
        </Links>
        , and the principles outlined in{" "}
        <Links target="_blank" rel="noopener noreferrer" href="https://www.senseandrespondpress.com/managing-outcomes">
          Outcomes over Output
        </Links>{" "}
        .
      </p>
      <Rule spacing="medium" inverted />

      <Stack spacing="var(--spacing-xx-large)">
        <RoadmapColumnBox>
          <ColumnHeading
            titleNumber="1."
            title="Discovery"
            paragraph="
      The most important part of curating the roadmap was to visit the MYOB offices and run workshops to understand the pain-points within the Front-end space at MYOB."
          />
          <Stack spacing="var(--spacing-large)">
            <Image dark src={DesignSystemsProcessDiagram} alt="Design system process diagram" />

            <Box>
              <Heading variant="small">1. Process Diagrams</Heading>
              <p>
                In our workshops across MYOB, we asked the Front-end participants to map out the end-to-end journey when
                building experiences at MYOB. Once the journeys were mapped out, we asked a representative from each
                group to talk through the process. From the qualitative feedback collected, we were then able to begin
                the next phase of curating our roadmap.
              </p>
              <Heading variant="smallest" style={{ marginBottom: "var(--spacing-small)" }}>
                The format of the workshops:
              </Heading>
              <ul style={{ margin: 0 }}>
                <li>In groups, map out the current end-to-end journey when building products at MYOB.</li>
                <li>Highlight pain points within the process</li>
                <li>List some ideas on how to improve the areas of frustration</li>
              </ul>
            </Box>

            <DiscoveryCluesInsights>
              <Stack spacing="var(--spacing-large)">
                <Box>
                  <Heading variant="small">2. Organise Clues</Heading>
                  <p>
                    The notes, audio recordings and process maps were analysed to uncover clues. I organised the
                    findings into horizontal <Do> Do</Do>,<Feel> Feel</Feel> & <Think>Think</Think> lanes, with each
                    note capturing a single customer point. Together, these lanes contributed to the development of
                    overarching insights.
                  </p>
                </Box>
                <Box>
                  <Heading variant="small">3. Distill Insights</Heading>
                  <p>
                    The next task was to distil the lanes into clear insights. I grouped related clues into vertical
                    lanes based on emerging themes. This structure helped surface key patterns, allowing me to craft a
                    single sentence that captured the essence of each theme. These summaries were then placed in the{" "}
                    <Insight> Insight</Insight> column for easy reference when shaping the roadmap.
                  </p>
                </Box>
              </Stack>

              <Image dark src={DesignSystemsDistillInsights} alt="Design system distilling insights" />
            </DiscoveryCluesInsights>
          </Stack>
        </RoadmapColumnBox>
        <RoadmapColumnBox>
          <ColumnHeading
            titleNumber="2."
            title="Shape the Roadmap"
            paragraph="With the discovery phase distilled into key Insights and Themes, we gained a clearer understanding of the high-level challenges within the Design System. The next step was translating these findings into actionable objectives that would inform our Roadmap."
          />
          <Stack spacing="var(--spacing-large)">
            <Stack spacing="var(--spacing-medium)">
              <Image dark src={DesignSystemsRoadmapPlan} alt="Prototyping the roadmap" spacing="large" />
              <p>
                We used Airtable to organise our findings by categorising insights under relevant themes and linking
                them to our draft roadmap items. This approach helped clearly illustrate the relationship between the
                problems identified during discovery and the objectives we planned to address. It also made it easier
                for team members and stakeholders to understand the rationale behind our strategic focus areas.
              </p>
            </Stack>

            <Box>
              <Heading variant="small">1. Insights</Heading>
              <ul>
                <li>We grouped and categorised all insights uncovered during the discovery phase within Airtable.</li>
                <li>
                  These insights, along with their overarching themes, served as the foundation for defining objectives
                  that would feed into our quarterly planning for the Design System team.
                </li>
              </ul>
            </Box>
            <Box>
              <Heading variant="small" style={{ color: "#70baff" }}>
                2. Roadmap Items (Objectives)
              </Heading>
              <ul>
                <li>Each Roadmap Item is directly informed by the insights and themes from the discovery phase.</li>
                <li>
                  As we defined and refined these items, we mapped specific insights to each objective card in Airtable.
                  This provided transparency and clear justification for our decisions.
                </li>
                <li>The themes driving each objective came from recurring patterns in discovery.</li>
                <li>Each roadmap item includes OKRs to help us measure progress and demonstrate impact.</li>
              </ul>
              <ObjectiveExample>
                <p>
                  <strong>Title:</strong> Unify Component Repositories
                </p>
                <p>
                  <strong>Objective:</strong> Migrate core styles into the widgets package to streamline development and
                  contribution workflows across teams. This will help reduce friction and make it easier for teams to
                  contribute new components to the Design System.
                </p>
                <p>
                  <strong>Insight:</strong> Engineers expressed frustration with managing two separate repositories. One
                  for components (JS) and one for styles (CSS); which slowed progress and discouraged contributions to
                  the shared system.
                </p>

                <p style={{ marginBottom: 12 }}>
                  <strong>Key Results:</strong>
                </p>
                <ul>
                  <li>Complete migration of shared styles into the widget package.</li>
                  <li>Reduce average time to contribute a new component by 30%.</li>
                  <li>Increase cross-team contributions to the system by 50% quarter-over-quarter.</li>
                  <li>Collect feedback from 3+ engineers to validate the impact of the migration.</li>
                </ul>
              </ObjectiveExample>
            </Box>

            <Box>
              <Heading variant="small" style={{ color: "#6db52c" }}>
                3. Backlog Items
              </Heading>
              <ul>
                <li>
                  Before we had a dedicated Design System team, there was a communal backlog of tasks. We wanted to
                  ensure we captured the items that aligned with relevant themes and made sense to carry forward.
                </li>
                <li>
                  We reviewed and mapped relevant backlog items to the new roadmap objectives. This allowed us to
                  prioritise meaningful work while still making use of previously identified opportunities or
                  “nice-to-haves.”
                </li>
                <li>
                  By taking this approach, we ensured our work was intentional and strategic, avoiding reactive or
                  disconnected task planning.
                </li>
              </ul>
            </Box>
          </Stack>
        </RoadmapColumnBox>
        <RoadmapColumnBox>
          <ColumnHeading
            titleNumber="3."
            title="Outcome Roadmap"
            paragraph="
        With priorities established and roadmap items defined, the next step was to align each objective to a strategic theme within a quarterly-based roadmap."
          />

          <Stack spacing="var(--spacing-medium)">
            <Image dark src={OutcomeBasedRoadmapImage} alt="Outcome-based roadmap" caption="" spacing="large" />
            <Box>
              <p>
                We adopted the Outcome Roadmap format inspired by Jeff Gothelf, structured around a Strategic Theme,
                supporting Objectives, and measurable Key Results. The roadmap uses a simple lane format with Upcoming,
                Doing, and Finished columns to track the duration and progress of each cycle. Each objective includes
                clear outcomes to demonstrate progress and indicate whether key customer behaviours are being positively
                influenced.
              </p>

              <p>
                While our goal was to complete two objectives per quarter, we often included additional objectives to
                allow flexibility based on team capacity and shifting priorities.
              </p>
            </Box>

            <RoadmapExample>
              <p>
                <strong>Strategic Theme:</strong> Consistency and Scalability
              </p>
              <p>
                <strong>Theme Description:</strong> Ensure design and development consistency across products and
                platforms, enabling the system to scale as the organisation grows.
              </p>
              <p>
                <strong>Time Period:</strong> Q1, 2019
              </p>
            </RoadmapExample>

            <ObjectiveExample>
              <p>
                <strong>Title:</strong> Automate the UIKit in Feelix
              </p>
              <p>
                <strong>Objective:</strong> Automate the Product Design UI Kit so it maintains a 1:1 match with the
                components available in the Feelix Design System. This alignment ensures Designers and Engineers are
                always working from the same source of truth.
              </p>
              <p>
                <strong>Insight:</strong> Enhancing or creating new Feelix components requires Visual Design to manually
                update the Product Design UIKit. Maintaining a perfect match between design tooling and the live system
                is essential to avoid misalignment and unnecessary debate around deviations.
              </p>

              <p style={{ marginBottom: 12 }}>
                <strong>Key Results:</strong>
              </p>
              <ul>
                <li>100% of components available in the chosen design tool</li>
                <li>60% of collected design feedback is positive</li>
                <li>Time spent by Visual Design updating the UI Kit is reduced to zero hours.</li>
                <li>60% of developer feedback is positive and outweighs negative responses</li>
              </ul>
            </ObjectiveExample>
          </Stack>
        </RoadmapColumnBox>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default DesigningTheRoadmap;
